<template>
  <div class="container-layout" :class="{ 'small-container': isSmallContainer }">
    <div :class="$style.header">
      <project-header
        :project="project"
        :language="getLanguage"
      />
    </div>

    <div v-if="isLoaded" class="bills-layout">
      <masonry grid-gap="20px" :col-width="columnWidth" :items="catalog.items">
        <bill-card
          v-for="bill in catalog.items"
          :bill="bill"
          :key="bill.id"
          @click="handleClickToBill"
        />
      </masonry>
    </div>

    <div v-show="loading" :class="$style.loader">
      <base-loader />
    </div>

    <base-footer />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isMobile, isTablet } from 'mobile-device-detect';

import throttle from 'lib/utils/throttle';
import serializeParams from 'lib/utils/url';
import { waitForTimeout } from 'lib/utils/await';

import HTTPService from '@/services/Http';

import Masonry from '@/components/Masonry';

import BaseLoader from 'atoms/base/BaseLoader';
import BaseFooter from 'molecules/base/BaseFooter';
import BillCard from 'organisms/project/BillCard';
import ProjectHeader from 'organisms/project/ProjectHeader';

// TODO: если данные расчеты потребуются где-нибудь еще, вынести в lib/utils/DOM.js
function isOffsetExceedsOrEqualsHeight() {
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop || window.pageYOffset || 0;
  const offset = scrollTop + window.innerHeight + 60;
  const height = document.documentElement.offsetHeight;

  return offset >= height;
}

export default {
  name: 'MarketplaceBills',
  components: {
    Masonry,
    BaseLoader,
    BaseFooter,
    BillCard,
    ProjectHeader,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  created() {
    this.loadData();
  },
  mounted() {
    window.addEventListener('scroll', throttle(this.handleScroll, 500));
  },
  beforeDestroy() {
    window.removeEventListener('scroll', throttle(this.handleScroll, 500));
  },
  data() {
    return {
      loading: false,
      catalog: {
        items: [],
        lastDataChunk: [],
        countPerPage: 0,
        internalOffset: 0,
      },
      documentHeight: 0,
      documentOffset: 0,
      isMobile,
      isTablet,
    };
  },
  computed: {
    isLoaded() {
      return this.$validate.isNotEmptyArray(this.catalog.items);
    },
    isSmallContainer() {
      return this.catalog.items.length <= 2;
    },
    columnWidth() {
      if (this.isTablet) {
        return 'minmax(Min(12em, 100%), 1fr)';
      }

      return this.isMobile ? 'minmax(Min(28vw, 100%), 1fr)' : '220px';
    },
    getPayload() {
      const getParams = Object.assign({},
        { projectId: this.project.project_id },
        { internalOffset: this.catalog.internalOffset }
      );

      return serializeParams(getParams);
    },
    getDonateCard() {
      return {
        billTariffs: [{
          amount: null,
          currency: this.project.currency,
        }],
        description: null,
        projectLogoLink: this.project.project_logo,
        projectName: this.project.project_name,
        billImageLink: null,
        isDonate: true,
      };
    },
    getWidgets() {
      return this.project.widgets.map(widget => this.getPreparedWidgetCard(widget));
    },
    isLastDataChunk() {
      return this.catalog.lastDataChunk.length < this.catalog.countPerPage;
    },
    ...mapGetters(['getLanguage']),
  },
  methods: {
    loadData() {
      this.catalog.items.push(...this.getWidgets);
      this.loadBills();
    },
    async loadBills() {
      this.loading = true;

      try {
        const { data } = await HTTPService.fetchMarketplaceBills(this.getPayload);

        await waitForTimeout(2000);
        this.catalog.items.push(...data.result);
        this.catalog.lastDataChunk = data.result;
        this.catalog.countPerPage = data.countPerPage;
        this.catalog.internalOffset = data.internalOffset;
      } catch (error) {
        console.error(error);
      }

      this.catalog.items.push(this.getDonateCard);
      this.loading = false;
    },
    getPreparedWidgetCard(widget) {
      const tariff = {
        amount: widget.amountGoal,
        currentProgress: widget.currentProgress,
        currentProgressPercent: widget.currentProgressPercent,
        currency: this.project.currency,
      };

      const attachment = widget.attachment || {};

      return {
        billTariffs: [tariff],
        description: widget.description || null,
        projectLogoLink: this.project.project_logo,
        projectName: this.project.project_name,
        billImageLink: attachment.link || null,
        widgetId: widget.id,
      };
    },
    handleClickToBill(bill) {
      const { payLink } = bill;

      if (this.$validate.isUndefined(payLink)) {
        this.$emit('donate');
        return;
      }

      window.location.assign(payLink);
    },
    async handleScroll() {
      if (this.loading || this.isLastDataChunk) {
        return;
      }

      if (isOffsetExceedsOrEqualsHeight()) {
        this.removeDonateCard();
        await this.loadBills();
      }
    },
    removeDonateCard() {
      const { items } = this.catalog;
      const filteredCatalog = items.filter(item => !item.isDonate);

      this.catalog.items = filteredCatalog;
    },
  },
};
</script>

<style lang="sass" module>
  .header
    width: 100%
    padding: 30px 0

    +respond-to('medium')
      padding: 40px 0

  .loader
    +flexbox
    +justify-content(center)
</style>

<style lang="sass" scoped>
  .container-layout
    width: 100%
    max-width: 740px
    margin: 0 auto 40px
    padding: 0 20px
    overflow-x: hidden

    @media screen and (max-width: 740px)
      max-width: 500px

    &.small-container
      max-width: 500px

  .bills-layout
    width: 100%
    margin: 0 auto
    margin-bottom: 30px

  @media screen and (max-width: 320px)
    .donate-button-header
      display: none

  @media screen and (min-width: 321px) and (max-width: 570px)
    .donate-button-header
      display: none
</style>
